// The place card. Controls (its parts): card.gallery, card.interior, card.booking, card.stepper.
// The gallery and the booking button are track T5's elements (gallery.js, booking.js, handed in by ui.js): the card
// only places them.
import { html, esc, raw, safeUrl, safeColor } from '../dom.js';
import { placeForKey, CATEGORIES } from '../content.js';
import { icon, catIcon } from './icons.js';
import { guestsText } from './layout.js';

const PLAY = raw('<i><svg class="ic" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l10.5-6.5z"/></svg></i>');
const GLOBE = raw('<i><svg class="ic" viewBox="0 0 24 24" aria-hidden="true"><ellipse cx="12" cy="12" rx="9" ry="3.6"/><path d="M12 3c2.6 2.4 2.6 15.6 0 18M12 3c-2.6 2.4-2.6 15.6 0 18"/></svg></i>');

/** @param hooks { renderGallery, bookingButton }: T5's two card hooks; `debug`: show the "to be confirmed" note */
export function cardParts(c, { renderGallery, bookingButton, debug = false }) {
  const { api, T, tx, i18n } = c;
  const hook = { runtime: c.rt, ui: T }; // what gallery.js and booking.js get: the runtime and the interface texts
  const part = (id) => (c.slots.card || []).includes(id);
  const siblings = (p) => (p.unit && c.units() && part('card.stepper') ? c.unitKeys(p.unit) : []);

  /** The card of a place (`placeForKey`), without T5's two elements. */
  function markup(p) {
    const cat = CATEGORIES.find((x) => x.id === p.category), title = c.title(p);
    const stats = p.unit ? [[T('card.size'), tx(p.size)], [T('card.guests'), guestsText(p, 'stat', i18n)], [T('card.floors'), tx(p.floors)]] : [];
    const facts = p.unit ? [[T('card.beds'), tx(p.beds)], ...(p.built ? [[T('card.built'), tx(p.built)]] : [])] : (p.facts || []).map(([k, v]) => [tx(k), tx(v)]);
    const sib = siblings(p), idx = sib.indexOf(p.id), heroImg = safeUrl(p.image);
    const inside = part('card.interior') && c.v('interiors.show') !== false && api.hasInterior?.(p.id);
    const act = !api.hasInterior?.(p.id) && api.cardAction?.(p.id); // WS-13: a card action of the map itself (a track's "Watch a race")
    return html`<div class="core">
      <div class="hero">${heroImg ? html`<img src="${heroImg}" alt="">` : html`<div class="hero-fallback"></div>`}
        ${c.layout.panel ? '' : html`<button class="round-btn glass close" aria-label="${T('card.close')}">${icon('close')}</button>`}
        ${p.unit && c.units() ? html`<span class="unit-no">${p.number}</span>` : ''}
        ${act ? html`<button class="step-inside card-action" aria-label="${T('card.action', { label: act.label, title })}">${PLAY}<span>${esc(act.label)}</span>${act.sub ? html`<small>${esc(act.sub)}</small>` : ''}</button>` : ''}
        ${inside ? html`<button class="step-inside" aria-label="${T(api.interiorLabel?.(p.id) === '3D' ? 'card.inside.3d' : 'card.inside.360', { title })}">${GLOBE}<span>${T('card.inside')}</span></button>` : ''}
      </div>
      <div class="body">
        <span class="eyebrow pill" style="--c:${safeColor(cat?.color, '#c9a961')}">${catIcon(p.category)}${tx(cat?.label)}</span>
        <h2>${title}</h2>
        ${Array.isArray(p.seasons) && !p.seasons.includes(c.season) ? html`<p class="off-season">${tx(p.offSeason) || T('card.offSeason', { seasons: i18n.list(p.seasons.map(c.seasonName)), when: T(`season.when.${c.season}`) })}</p>` : ''}
        ${stats.length ? html`<div class="stats">${stats.map(([k, v]) => html`<div><small>${k}</small><b>${v}</b></div>`)}</div>` : ''}
        <p class="blurb">${tx(p.blurb)}</p>
        ${facts.length ? html`<dl>${facts.map(([k, v]) => html`<dt>${k}</dt><dd>${v}</dd>`)}</dl>` : ''}
        ${p.features?.length ? html`<ul>${p.features.map((f) => html`<li>${icon('check')}<span>${tx(f)}</span></li>`)}</ul>` : ''}
        <div class="actions">
          ${sib.length > 1 ? html`<div class="stepper"><button class="round-btn small" data-step="-1" aria-label="${T('card.prev')}">${icon('prev')}</button><span>${T('card.step', { i: i18n.num(idx + 1), n: i18n.num(sib.length) })}</span><button class="round-btn small" data-step="1" aria-label="${T('card.next')}">${icon('next')}</button></div>` : ''}
        </div>
        ${debug && p.confirm ? html`<p class="confirm">${T('card.confirm')}</p>` : ''}
      </div></div>`;
  }

  // VIS-128: an open card moves the top-right buttons left of it (style.css). Where they would then sit on the brand or
  // the season switch (a narrow window, a portrait tablet), the tour button keeps its icon alone and, still too wide,
  // the row takes a line of its own under the brand (the explore panel moves down by that row): <body data-top="icons|low">.
  function fitTop() {
    const d = document.body.dataset, q = (s) => document.querySelector(s), ta = q('.top-actions'), tl = q('.top-left'), card = q('#card.open');
    delete d?.top; // (the unit tests' page stand has no dataset)
    if (ta && tl && card?.offsetWidth && !c.layout.panel && innerWidth > 760) for (const k of ['icons', 'low']) if (card.offsetLeft - 32 - ta.offsetWidth < tl.getBoundingClientRect().right) d.top = k;
  }
  globalThis.addEventListener?.('resize', fitTop); // (a markup stand without a page, the unit tests, has none)

  // VIS-230: on a phone the card opens compact (the photo, title, one line, two lines of text, the actions) and grows to
  // the room under the rail (phone.js --ph-room) when the visitor asks: a press on its handle, or a scroll inside it (the
  // handle lies at the top of the card's own scroller, so dragging it up scrolls). A press makes it compact again.
  function grip(card) {
    const core = card.firstElementChild, g = Object.assign(document.createElement('button'), { className: 'ph-grip', ariaLabel: T('ph.card.more') });
    const full = (v) => { document.body.classList.toggle('ph-card-full', v); g.ariaExpanded = v; if (!v) core.scrollTop = 0; };
    g.onclick = () => full(g.ariaExpanded !== 'true');
    core.addEventListener('scroll', () => core.scrollTop > 8 && full(true), { passive: true });
    core.prepend(g);
    full(false);
  }

  /** Draws the card of a place into #card and opens it; null closes it. */
  function render(p) {
    const card = document.querySelector('#card'), body = document.body;
    if (!card) return;
    if (!p) { card.classList.remove('open'); body.classList.remove('card-open', 'ph-card-full'); fitTop(); return; }
    card.innerHTML = html`${markup(p)}`;
    if (c.phone) grip(card); // (never a layout with a panel: phone.js phoneOn)
    const photos = part('card.gallery') && renderGallery(p, hook); // T5: the place's photos, in place of the one picture
    if (photos) card.querySelector('.hero').firstElementChild.replaceWith(photos);
    card.classList.toggle('no-photo', !!card.querySelector('.hero-fallback')); // no photo at all: no picture (style.css)
    const book = part('card.booking') && c.v('booking.show') !== false && bookingButton(p, hook); // T5: "Book now" / "Discover more"
    if (book) card.querySelector('.actions').prepend(book);
    const close = card.querySelector('.close');
    if (close) close.onclick = () => api.select(null);
    const inside = card.querySelector('.step-inside:not(.card-action)'); // WS-3: open the 360° interior tour (WS-7: splat interiors too)
    if (inside) inside.onclick = () => api.enterInterior(p.id, null, inside);
    const act = card.querySelector('.card-action');
    if (act) act.onclick = () => { const run = api.cardAction(p.id)?.run; api.select(null); run?.(); }; // card closes so the view is free
    // WS-91: walking directions to the place, where the page offers "Show where I am" (lazy/locate.js)
    const go = api.locate?.directions(p.id, c.title(p));
    if (go) {
      const b = Object.assign(document.createElement('button'), { type: 'button', className: 'card-route' });
      b.innerHTML = html`${icon('route')}<span>${T('card.directions')}</span>`;
      b.onclick = go;
      const row = card.querySelector('.actions');
      row.insertBefore(b, row.querySelector('.stepper')); // after the booking button, before the stepper
    }
    const sib = siblings(p), idx = sib.indexOf(p.id);
    card.querySelectorAll('[data-step]').forEach((b) => (b.onclick = () => api.select(sib[(idx + Number(b.dataset.step) + sib.length) % sib.length], true)));
    card.classList.remove('open');
    void card.offsetWidth;
    card.classList.add('open');
    body.classList.add('card-open');
    fitTop();
  }

  return {
    parts: { 'card.gallery': {}, 'card.interior': {}, 'card.booking': {}, 'card.stepper': {} },
    markup, render,
    /** Draws the card of the selected place again (season, language, a part shown or hidden). */
    refresh(key) { const p = key && placeForKey(key); if (p) render(p); },
  };
}
